// Mock data service for partner dashboard demo mode
// This file provides realistic mock data that matches the API response formats

import type { UserListItem, PartnerProduct } from "./api"

// Storage key for persisting mock data
const MOCK_DATA_STORAGE_KEY = "partner_dashboard_mock_data"

// Interface for mock data storage structure
interface MockDataStorage {
  users: UserListItem[]
  advisorStats: {
    total_users: number
    open_task_count: number
    auto_applied_count: number
    hr_messages_count: number
  }
  applicantStats: Record<string, {
    documents_count: number
    jobs_searched: number
    applications_sent: number
    hr_emails_received: number
  }>
  jobEngines: Record<string, any>
  files: Record<string, Array<{
    file_name: string
    file_size: number
    upload_date: string
    file_type: string
    download_url?: string
    file_id?: string
  }>>
  messages: Record<string, Array<{
    id: string
    subject: string
    body: string
    created_at: string
    seen: boolean
    direction: "inbound" | "outbound"
    from?: string
    attachment?: string
  }>>
  notifications: Array<{
    id: string
    user_id: string
    created_at: string
    message: string
    Delivery: boolean
    message_type?: string
  }>
  jobHistory: Record<string, Array<{
    applicant_id: string
    job_id: string
    specifications: any
    created_at?: string
  }>>
  jobMessages: Record<string, Array<{
    id: string
    created_at: string
    message_id: string
    in_reply_to: string | null
    subject: string
    sender: string
    body: string
    thread_id: string
    inbox_id: string
    seen: boolean
    direction: "inbound" | "outbound"
    attachment: string | null
    to: string
    job_id: string
  }>>
  messageThreads: Record<string, Array<{
    thread_id: string
    company?: string
    logo?: string
    position?: string
    from_email?: string
    lastMessage?: string
    timestamp?: string
    unread?: boolean
    messages: Array<{
      id: string
      subject: string
      body: string
      created_at: string
      seen: boolean
      direction: "inbound" | "outbound"
      from?: string
      attachment?: string
    }>
  }>>
  products: Record<string, PartnerProduct[]> // Key is user_id, value is array of products
}

// Generate initial mock users
function generateInitialMockUsers(): UserListItem[] {
  const now = new Date()
  const getDateString = (daysAgo: number) => {
    const date = new Date(now)
    date.setDate(date.getDate() - daysAgo)
    return date.toLocaleDateString("de-DE")
  }

  return [
    {
      id: "mock-user-1",
      first_name: "Max",
      last_name: "Mustermann",
      email: "max.mustermann@example.com",
      phone: "+491234567890",
      whatsapp: "+491234567890",
      subscription: "premium",
      status: "active",
      applications: {
        sent: 45,
        responses: 12,
        interviews: 3,
      },
      dateJoined: getDateString(30),
      lastActivity: "Heute",
      needsAttention: false,
      budget: {
        total: 20,
        used: 15,
      },
    },
    {
      id: "mock-user-2",
      first_name: "Anna",
      last_name: "Schmidt",
      email: "anna.schmidt@example.com",
      phone: "+491234567891",
      whatsapp: "+491234567891",
      subscription: "basic",
      status: "active",
      applications: {
        sent: 28,
        responses: 8,
        interviews: 2,
      },
      dateJoined: getDateString(15),
      lastActivity: "Gestern",
      needsAttention: true,
      budget: {
        total: 10,
        used: 8,
      },
    },
    {
      id: "mock-user-3",
      first_name: "Thomas",
      last_name: "Weber",
      email: "thomas.weber@example.com",
      phone: "+491234567892",
      whatsapp: "+491234567892",
      subscription: "premium",
      status: "active",
      applications: {
        sent: 67,
        responses: 18,
        interviews: 5,
      },
      dateJoined: getDateString(45),
      lastActivity: "Vor 2 Tagen",
      needsAttention: false,
      budget: {
        total: 20,
        used: 12,
      },
    },
    {
      id: "mock-user-4",
      first_name: "Sarah",
      last_name: "Müller",
      email: "sarah.mueller@example.com",
      phone: "+491234567893",
      whatsapp: "+491234567893",
      subscription: "basic",
      status: "paused",
      applications: {
        sent: 12,
        responses: 3,
        interviews: 1,
      },
      dateJoined: getDateString(60),
      lastActivity: "Vor 1 Woche",
      needsAttention: false,
      budget: {
        total: 10,
        used: 5,
      },
    },
    {
      id: "mock-user-5",
      first_name: "Michael",
      last_name: "Fischer",
      email: "michael.fischer@example.com",
      phone: "+491234567894",
      whatsapp: "+491234567894",
      subscription: "premium",
      status: "active",
      applications: {
        sent: 89,
        responses: 25,
        interviews: 7,
      },
      dateJoined: getDateString(20),
      lastActivity: "Heute",
      needsAttention: true,
      budget: {
        total: 20,
        used: 19,
      },
    },
  ]
}

// Generate initial advisor statistics
function generateInitialAdvisorStats(usersCount: number): MockDataStorage["advisorStats"] {
  return {
    total_users: usersCount,
    open_task_count: Math.floor(usersCount * 1.5),
    auto_applied_count: usersCount * 15,
    hr_messages_count: usersCount * 8,
  }
}

// Generate initial applicant statistics for a user
function generateInitialApplicantStats(userId: string): MockDataStorage["applicantStats"][string] {
  const baseStats = {
    documents_count: Math.floor(Math.random() * 3) + 1,
    jobs_searched: Math.floor(Math.random() * 200) + 50,
    applications_sent: Math.floor(Math.random() * 100) + 20,
    hr_emails_received: Math.floor(Math.random() * 30) + 5,
  }
  return baseStats
}

// Generate initial job engines
function generateInitialJobEngines(users: UserListItem[]): Record<string, any> {
  const engines: Record<string, any> = {}
  users.forEach((user) => {
    engines[user.id] = {
      job_parameters: {
        user_consent: true,
        job_location: ["Berlin", "München", "Hamburg"][Math.floor(Math.random() * 3)],
        job_keywords: ["Software Engineer", "Developer", "Full Stack"][Math.floor(Math.random() * 3)],
        job_type: ["fulltime", "parttime", "contract"][Math.floor(Math.random() * 3)],
      },
      job_match_threshold: 60 + Math.floor(Math.random() * 20),
    }
  })
  return engines
}

// Generate initial files for a user
// Uses real files from public/doc_mock directory
function generateInitialFiles(userId: string): Array<{
  file_name: string
  file_size: number
  upload_date: string
  file_type: string
  download_url?: string
  file_id?: string
}> {
  const files: Array<{
    file_name: string
    file_size: number
    upload_date: string
    file_type: string
    download_url?: string
    file_id?: string
  }> = []
  const date = new Date()
  
  // Use real files from public/doc_mock
  const mockFiles = [
    {
      file_name: "Resume.pdf",
      file_path: "/doc_mock/resume-sample.pdf",
      file_size: 245678, // Approximate size
      file_type: "application/pdf",
      upload_date: new Date(date.getTime() - 15 * 24 * 60 * 60 * 1000).toISOString(), // 15 days ago
    },
    {
      file_name: "Cover Letter.pdf",
      file_path: "/doc_mock/Cover-Letter-Sample.pdf",
      file_size: 189234, // Approximate size
      file_type: "application/pdf",
      upload_date: new Date(date.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(), // 10 days ago
    },
  ]

  // Add files with unique IDs
  mockFiles.forEach((file, index) => {
    files.push({
      file_name: file.file_name,
      file_size: file.file_size,
      upload_date: file.upload_date,
      file_type: file.file_type,
      download_url: file.file_path,
      file_id: `${userId}_file_${index + 1}`,
    })
  })

  return files
}

// Generate initial messages for a user
function generateInitialMessages(userId: string): Array<{
  id: string
  subject: string
  body: string
  created_at: string
  seen: boolean
  direction: "inbound" | "outbound"
  from?: string
  attachment?: string
}> {
  const messages = []
  const messageCount = Math.floor(Math.random() * 5) + 2

  for (let i = 0; i < messageCount; i++) {
    const date = new Date()
    date.setDate(date.getDate() - Math.floor(Math.random() * 14))
    const isInbound = Math.random() > 0.5

    messages.push({
      id: `msg-${userId}-${i + 1}`,
      subject: isInbound 
        ? `Re: Application for Software Engineer Position`
        : `Follow-up: Application Status`,
      body: isInbound
        ? `Thank you for your application. We would like to schedule an interview with you.`
        : `I wanted to follow up on my application submitted last week.`,
      created_at: date.toISOString(),
      seen: Math.random() > 0.3,
      direction: (isInbound ? "inbound" : "outbound") as "inbound" | "outbound",
      from: isInbound ? "hr@company.com" : undefined,
      attachment: undefined,
    })
  }

  return messages.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
}

// Generate initial notifications
function generateInitialNotifications(users: UserListItem[]): Array<{
  id: string
  user_id: string
  created_at: string
  message: string
  Delivery: boolean
  message_type?: string
}> {
  const notifications = []
  const notificationMessages = [
    "Neuer Bewerber Max Mustermann wurde hinzugefügt",
    "Anna Schmidt hat eine neue Bewerbung gesendet",
    "Thomas Weber hat eine Antwort von einem Arbeitgeber erhalten",
    "Sarah Müller benötigt Aufmerksamkeit - Dokumente fehlen",
    "Michael Fischer hat ein Interview geplant",
    "Neue Coaching-Sitzung mit Max Mustermann geplant",
    "System-Update: Neue Features verfügbar",
    "Wichtige Ressource hinzugefügt: Bewerbungsvorlagen",
  ]

  // Generate 5-8 notifications
  const notificationCount = Math.floor(Math.random() * 4) + 5
  for (let i = 0; i < notificationCount; i++) {
    const date = new Date()
    date.setHours(date.getHours() - Math.floor(Math.random() * 48))
    const randomUser = users[Math.floor(Math.random() * users.length)]
    
    notifications.push({
      id: `notif-${i + 1}`,
      user_id: randomUser?.id || "mock-user-1",
      created_at: date.toISOString(),
      message: notificationMessages[Math.floor(Math.random() * notificationMessages.length)],
      Delivery: Math.random() > 0.3,
      message_type: ["applicant", "system", "session", "resource"][Math.floor(Math.random() * 4)],
    })
  }

  return notifications.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
}

// Generate initial job history for a user
export function generateInitialJobHistory(userId: string): Array<{
  applicant_id: string
  job_id: string
  specifications: any
  created_at?: string
}> {
  const jobHistory = []
  const jobTitles = [
    "Senior Software Engineer",
    "Frontend Developer",
    "Full Stack Developer",
    "Backend Developer",
    "DevOps Engineer",
    "Product Manager",
    "UX Designer",
    "Data Scientist",
  ]
  const companies = [
    "TechCorp GmbH",
    "Innovation Labs",
    "Digital Solutions AG",
    "StartupHub",
    "Enterprise Systems",
    "Cloud Services GmbH",
    "AI Innovations",
    "Web Development Co.",
  ]
  const locations = ["Berlin", "München", "Hamburg", "Frankfurt", "Köln", "Stuttgart"]
  const statuses = ["applied", "pending", "interview", "offer", "rejected"]
  
  // Generate 10-15 jobs per user
  const jobCount = Math.floor(Math.random() * 6) + 10
  for (let i = 0; i < jobCount; i++) {
    const jobId = `job-${userId}-${i + 1}`
    const date = new Date()
    date.setDate(date.getDate() - Math.floor(Math.random() * 60))
    const status = statuses[Math.floor(Math.random() * statuses.length)]
    const matchScore = Math.floor(Math.random() * 30) + 70 // 70-100
    
    jobHistory.push({
      applicant_id: userId,
      job_id: jobId,
      specifications: {
        job_title: jobTitles[Math.floor(Math.random() * jobTitles.length)],
        company: companies[Math.floor(Math.random() * companies.length)],
        location: locations[Math.floor(Math.random() * locations.length)],
        job_type: ["fulltime", "parttime", "contract"][Math.floor(Math.random() * 3)],
        salary_range: `${Math.floor(Math.random() * 30) + 50}K - ${Math.floor(Math.random() * 30) + 80}K €`,
        application_status: status,
        average_score: matchScore,
        score_criteria: matchScore,
        job_date: date.toISOString(),
        posting_date: new Date(date.getTime() - Math.floor(Math.random() * 7) * 24 * 60 * 60 * 1000).toISOString(),
        applied_at: status === "applied" ? date.toISOString() : undefined,
        job_link: `https://example.com/jobs/${jobId}`,
        job_description: "We are looking for an experienced developer to join our team...",
        specific_details: {
          beruf: "Softwareentwicklung",
          refnr: `REF-${jobId}`,
          titel: jobTitles[Math.floor(Math.random() * jobTitles.length)],
          arbeitsort: {
            ort: locations[Math.floor(Math.random() * locations.length)],
            plz: `${Math.floor(Math.random() * 90000) + 10000}`,
            land: "Deutschland",
            region: "Deutschland",
            koordinaten: {
              lat: 52.52 + (Math.random() - 0.5) * 2,
              lon: 13.405 + (Math.random() - 0.5) * 2,
            },
          },
          arbeitgeber: companies[Math.floor(Math.random() * companies.length)],
          eintrittsdatum: new Date(date.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          kundennummerHash: `hash-${jobId}`,
          modifikationsTimestamp: date.toISOString(),
          job_resume_similarity_metrics: {
            cultural_fit: matchScore + Math.floor(Math.random() * 10) - 5,
            job_type_fit: matchScore + Math.floor(Math.random() * 10) - 5,
            experience_level: matchScore + Math.floor(Math.random() * 10) - 5,
            keyword_matching: matchScore + Math.floor(Math.random() * 10) - 5,
            project_relevance: matchScore + Math.floor(Math.random() * 10) - 5,
            skillset_matching: matchScore + Math.floor(Math.random() * 10) - 5,
            industry_relevance: matchScore + Math.floor(Math.random() * 10) - 5,
            job_title_similarity: matchScore + Math.floor(Math.random() * 10) - 5,
            language_proficiency: 90 + Math.floor(Math.random() * 10),
            educational_background: matchScore + Math.floor(Math.random() * 10) - 5,
            job_function_alignment: matchScore + Math.floor(Math.random() * 10) - 5,
          },
          aktuelleVeroeffentlichungsdatum: date.toISOString(),
        },
        how_to_apply: "Apply via our website",
        platform: "LinkedIn",
        selected: status === "applied",
      },
      created_at: date.toISOString(),
    })
  }

  return jobHistory.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
}

// Generate initial job messages for a specific job
export function generateInitialJobMessages(userId: string, jobId: string): Array<{
  id: string
  created_at: string
  message_id: string
  in_reply_to: string | null
  subject: string
  sender: string
  body: string
  thread_id: string
  inbox_id: string
  seen: boolean
  direction: "inbound" | "outbound"
  attachment: string | null
  to: string
  job_id: string
}> {
  const messages = []
  const threadId = `thread-${jobId}`
  const inboxId = `inbox-${userId}`
  const messageCount = Math.floor(Math.random() * 5) + 3 // 3-7 messages

  for (let i = 0; i < messageCount; i++) {
    const date = new Date()
    date.setHours(date.getHours() - (messageCount - i))
    const isInbound = i % 2 === 0
    const messageId = `msg-${jobId}-${i + 1}`
    const inReplyTo = i > 0 ? `msg-${jobId}-${i}` : null

      messages.push({
        id: messageId,
        created_at: date.toISOString(),
        message_id: messageId,
        in_reply_to: inReplyTo,
        subject: isInbound 
          ? `Re: Application for Software Engineer Position`
          : `Follow-up: Application Status`,
        sender: isInbound ? "hr@company.com" : "applicant@example.com",
        body: isInbound
          ? `Thank you for your application. We would like to schedule an interview with you. Please let us know your availability.`
          : `I wanted to follow up on my application submitted last week. I am very interested in this position and would appreciate any updates.`,
        thread_id: threadId,
        inbox_id: inboxId,
        seen: Math.random() > 0.3,
        direction: (isInbound ? "inbound" : "outbound") as "inbound" | "outbound",
        attachment: null,
        to: isInbound ? "applicant@example.com" : "hr@company.com",
        job_id: jobId,
      })
  }

  return messages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
}

// Generate initial message threads for inbox
export function generateInitialMessageThreads(userId: string): Array<{
  thread_id: string
  company?: string
  logo?: string
  position?: string
  from_email?: string
  lastMessage?: string
  timestamp?: string
  unread?: boolean
  messages: Array<{
    id: string
    subject: string
    body: string
    created_at: string
    seen: boolean
    direction: "inbound" | "outbound"
    from?: string
    attachment?: string
  }>
}> {
  const threads = []
  const companies = [
    "TechCorp GmbH",
    "Innovation Labs",
    "Digital Solutions AG",
    "StartupHub",
    "Enterprise Systems",
  ]
  const positions = [
    "Senior Software Engineer",
    "Frontend Developer",
    "Full Stack Developer",
    "Product Manager",
    "UX Designer",
  ]
  
  // Generate 3-5 threads per user - use deterministic count for consistency
  const threadCount = 4 // Fixed count for consistency
  for (let i = 0; i < threadCount; i++) {
    const threadId = `thread-${userId}-${i + 1}`
    const company = companies[i % companies.length] // Use modulo for deterministic selection
    const position = positions[i % positions.length]
    const fromEmail = `hr@${company.toLowerCase().replace(/\s+/g, "")}.com`
    
    // Create base date - ensure it's always valid
    const baseDate = new Date()
    baseDate.setDate(baseDate.getDate() - (i + 1)) // Each thread is 1 day older
    baseDate.setHours(10 + i) // Different hours
    baseDate.setMinutes(30)
    baseDate.setSeconds(0)
    baseDate.setMilliseconds(0)
    
    // Generate 2-4 messages per thread - ensure at least 2 messages
    const messageCount = 3 // Fixed count for consistency
    const messages = []
    for (let j = 0; j < messageCount; j++) {
      const messageDate = new Date(baseDate)
      messageDate.setHours(messageDate.getHours() + j)
      messageDate.setMinutes(messageDate.getMinutes() + (j * 15))
      const isCompany = j % 2 === 0
      
      const messageText = isCompany
        ? `Thank you for your application for the ${position} position. We would like to schedule an interview.`
        : `I am very interested in this position and would appreciate the opportunity to discuss it further.`
      
      // Ensure created_at is always a valid ISO string
      const created_at = messageDate.toISOString()
      
      messages.push({
        id: `msg-${threadId}-${j + 1}`,
        subject: isCompany ? `Re: Application for ${position}` : `Follow-up: ${position}`,
        body: messageText,
        created_at: created_at,
        seen: j === messageCount - 1 ? false : true, // Last message is unread
        direction: (isCompany ? "inbound" : "outbound") as "inbound" | "outbound",
        from: isCompany ? fromEmail : undefined,
        attachment: undefined,
      })
    }
    
    // Sort messages by created_at (oldest first) - ensure proper order
    messages.sort((a, b) => {
      const timeA = new Date(a.created_at).getTime()
      const timeB = new Date(b.created_at).getTime()
      return timeA - timeB
    })
    
    // Ensure we have at least one message
    if (messages.length === 0) {
      const fallbackDate = new Date(baseDate)
      messages.push({
        id: `msg-${threadId}-1`,
        subject: `Re: Application for ${position}`,
        body: `Thank you for your application for the ${position} position.`,
        created_at: fallbackDate.toISOString(),
        seen: false,
        direction: "inbound" as const,
        from: fromEmail,
        attachment: undefined,
      })
    }
    
    // Get first and last message for thread metadata
    const firstMessage = messages[0]
    const lastMessage = messages[messages.length - 1]
    
    threads.push({
      thread_id: threadId,
      company,
      logo: `/placeholder.svg?height=40&width=40&text=${company[0]}`,
      position,
      from_email: fromEmail,
      lastMessage: lastMessage.body,
      timestamp: firstMessage.created_at, // Use first message timestamp for thread
      unread: messages.some((m) => !m.seen),
      messages,
    })
  }
  
  // Sort threads by timestamp (newest first) - ensure proper sorting
  return threads.sort((a, b) => {
    if (!a.timestamp || !b.timestamp) return 0
    const timeA = new Date(a.timestamp).getTime()
    const timeB = new Date(b.timestamp).getTime()
    if (isNaN(timeA) || isNaN(timeB)) return 0
    return timeB - timeA
  })
}

// Generate initial mock products for a user
function generateInitialMockProducts(userId: string): PartnerProduct[] {
  const now = new Date()
  const getDateString = (daysAgo: number) => {
    const date = new Date(now)
    date.setDate(date.getDate() - daysAgo)
    return date.toISOString()
  }

  return [
    {
      product_id: `product-${userId}-1`,
      user_id: userId,
      product_name: "Advanced React Course",
      product_description: "A comprehensive course for learning React with practical projects and real-world exercises",
      product_link: "https://example.com/react-course",
      product_metadata: {},
      created_at: getDateString(10),
      updated_at: getDateString(10),
    },
    {
      product_id: `product-${userId}-2`,
      user_id: userId,
      product_name: "Complete TypeScript Guide Book",
      product_description: "A comprehensive book for learning TypeScript from beginner to advanced level",
      product_link: "https://example.com/typescript-book",
      product_metadata: {},
      created_at: getDateString(5),
      updated_at: getDateString(5),
    },
    {
      product_id: `product-${userId}-3`,
      user_id: userId,
      product_name: "Modern UI/UX Design Webinar",
      product_description: "Online webinar about user interface and user experience design principles",
      product_link: "https://example.com/uiux-webinar",
      product_metadata: {},
      created_at: getDateString(2),
      updated_at: getDateString(2),
    },
  ]
}

// Initialize mock data storage
function initializeMockData(): MockDataStorage {
  const users = generateInitialMockUsers()
  const advisorStats = generateInitialAdvisorStats(users.length)
  const applicantStats: Record<string, any> = {}
  const jobEngines = generateInitialJobEngines(users)
  const files: Record<string, any[]> = {}
  const messages: Record<string, any[]> = {}
  const notifications = generateInitialNotifications(users)
  const jobHistory: Record<string, any[]> = {}
  const jobMessages: Record<string, any[]> = {}
  const messageThreads: Record<string, any[]> = {}
  const products: Record<string, PartnerProduct[]> = {}

  users.forEach((user) => {
    applicantStats[user.id] = generateInitialApplicantStats(user.id)
    files[user.id] = generateInitialFiles(user.id)
    messages[user.id] = generateInitialMessages(user.id)
    jobHistory[user.id] = generateInitialJobHistory(user.id)
    // Generate messages for each job in job history
    jobHistory[user.id].forEach((job: any) => {
      const jobKey = `${user.id}_${job.job_id}`
      jobMessages[jobKey] = generateInitialJobMessages(user.id, job.job_id)
    })
    // Generate message threads for inbox
    messageThreads[user.id] = generateInitialMessageThreads(user.id)
    // Generate initial products for partner users (users with role 'partner' or 'advisor')
    if (user.role === "partner" || user.role === "advisor") {
      products[user.id] = generateInitialMockProducts(user.id)
    }
  })

  return {
    users,
    advisorStats,
    applicantStats,
    jobEngines,
    files,
    messages,
    notifications,
    jobHistory,
    jobMessages,
    messageThreads,
    products,
  }
}

// Load mock data from localStorage or initialize
export function loadMockData(): MockDataStorage {
  if (typeof window === "undefined") {
    return initializeMockData()
  }

  try {
    const stored = localStorage.getItem(MOCK_DATA_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      // Ensure all required fields exist
      if (parsed.users && Array.isArray(parsed.users)) {
        // Check if jobHistory and jobMessages exist, if not, initialize them
        if (!parsed.jobHistory || typeof parsed.jobHistory !== "object") {
          parsed.jobHistory = {}
        }
        if (!parsed.jobMessages || typeof parsed.jobMessages !== "object") {
          parsed.jobMessages = {}
        }
        
        // Ensure jobHistory exists for all users
        // Also regenerate files to ensure they use the real files from public/doc_mock
        parsed.users.forEach((user: any) => {
          // Regenerate files for all users to ensure they use real files (Resume.pdf and Cover Letter.pdf)
          if (!parsed.files || !parsed.files[user.id] || parsed.files[user.id].length === 0) {
            if (!parsed.files) parsed.files = {}
            parsed.files[user.id] = generateInitialFiles(user.id)
          } else {
            // Check if files have download_url pointing to real files, if not, regenerate them
            const userFiles = parsed.files[user.id] || []
            const hasRealFiles = userFiles.some((f: any) => f.download_url && (f.download_url.includes('/doc_mock/resume-sample.pdf') || f.download_url.includes('/doc_mock/Cover-Letter-Sample.pdf')))
            // Also check if we have both Resume and Cover Letter
            const hasResume = userFiles.some((f: any) => f.file_name && (f.file_name.includes('Resume') || f.file_name.includes('resume')))
            const hasCoverLetter = userFiles.some((f: any) => f.file_name && (f.file_name.includes('Cover') || f.file_name.includes('cover')))
            if (!hasRealFiles || !hasResume || !hasCoverLetter) {
              parsed.files[user.id] = generateInitialFiles(user.id)
            }
          }
          
          if (!parsed.jobHistory[user.id]) {
            parsed.jobHistory[user.id] = generateInitialJobHistory(user.id)
            // Generate messages for each job
            parsed.jobHistory[user.id].forEach((job: any) => {
              const jobKey = `${user.id}_${job.job_id}`
              if (!parsed.jobMessages[jobKey]) {
                parsed.jobMessages[jobKey] = generateInitialJobMessages(user.id, job.job_id)
              }
            })
          }
          // Ensure messageThreads exists for all users
          if (!parsed.messageThreads || typeof parsed.messageThreads !== "object") {
            parsed.messageThreads = {}
          }
          if (!parsed.messageThreads[user.id]) {
            parsed.messageThreads[user.id] = generateInitialMessageThreads(user.id)
          }
          
          // Initialize products for partner/advisor users
          if (!parsed.products) {
            parsed.products = {}
          }
          if ((user.role === "partner" || user.role === "advisor") && !parsed.products[user.id]) {
            parsed.products[user.id] = generateInitialMockProducts(user.id)
          }
        })
        
        // Save updated data back to localStorage
        saveMockData(parsed as MockDataStorage)
        
        return parsed as MockDataStorage
      }
    }
  } catch (error) {
    console.warn("Failed to load mock data from localStorage:", error)
  }

  const initialData = initializeMockData()
  saveMockData(initialData)
  return initialData
}

// Save mock data to localStorage
export function saveMockData(data: MockDataStorage): void {
  if (typeof window === "undefined") return

  try {
    localStorage.setItem(MOCK_DATA_STORAGE_KEY, JSON.stringify(data))
  } catch (error) {
    console.warn("Failed to save mock data to localStorage:", error)
  }
}

// Get mock data (singleton pattern)
let mockDataCache: MockDataStorage | null = null

export function getMockData(): MockDataStorage {
  if (!mockDataCache) {
    mockDataCache = loadMockData()
  }
  return mockDataCache
}

// Force reload mock data (useful after updates)
export function reloadMockData(): MockDataStorage {
  mockDataCache = null
  return getMockData()
}

// Update mock data and save
export function updateMockData(updater: (data: MockDataStorage) => MockDataStorage): void {
  const currentData = getMockData()
  const updatedData = updater(currentData)
  mockDataCache = updatedData
  saveMockData(updatedData)
}

// Reset mock data to initial state
export function resetMockData(): void {
  if (typeof window !== "undefined") {
    localStorage.removeItem(MOCK_DATA_STORAGE_KEY)
  }
  mockDataCache = null
}

// Clear all demo mode data (mock data and demo mode flags)
export function clearDemoModeData(): void {
  if (typeof window !== "undefined") {
    // Clear mock data from localStorage
    localStorage.removeItem(MOCK_DATA_STORAGE_KEY)
    
    // Clear demo mode flags from sessionStorage and localStorage
    sessionStorage.removeItem("demo_mode")
    localStorage.removeItem("demo_mode")
    
    // Reset cache
    mockDataCache = null
    
    console.log("[Demo] All demo mode data cleared")
  }
}

// Helper functions for specific data operations
export function addMockUser(user: UserListItem): void {
  updateMockData((data) => {
    const newUser = {
      ...user,
      id: user.id || `mock-user-${Date.now()}`,
    }
    
    // Generate initial job history and messages for the new user
    const jobHistory = generateInitialJobHistory(newUser.id)
    const messageThreads = generateInitialMessageThreads(newUser.id)
    
    // Generate job messages for each job in history
    const jobMessages: Record<string, any[]> = {}
    jobHistory.forEach((job: any) => {
      const jobKey = `${newUser.id}_${job.job_id}`
      jobMessages[jobKey] = generateInitialJobMessages(newUser.id, job.job_id)
    })
    
    return {
      ...data,
      users: [...data.users, newUser],
      advisorStats: {
        ...data.advisorStats,
        total_users: data.users.length + 1,
      },
      applicantStats: {
        ...data.applicantStats,
        [newUser.id]: generateInitialApplicantStats(newUser.id),
      },
      jobEngines: {
        ...data.jobEngines,
        [newUser.id]: generateInitialJobEngines([newUser])[newUser.id],
      },
      files: {
        ...data.files,
        [newUser.id]: generateInitialFiles(newUser.id),
      },
      messages: {
        ...data.messages,
        [newUser.id]: generateInitialMessages(newUser.id),
      },
      jobHistory: {
        ...data.jobHistory,
        [newUser.id]: jobHistory,
      },
      jobMessages: {
        ...data.jobMessages,
        ...jobMessages,
      },
      messageThreads: {
        ...data.messageThreads,
        [newUser.id]: messageThreads,
      },
    }
  })
}

export function updateMockUser(userId: string, updates: Partial<UserListItem>): void {
  updateMockData((data) => ({
    ...data,
    users: data.users.map((user) =>
      user.id === userId ? { ...user, ...updates } : user
    ),
  }))
}

export function removeMockUser(userId: string): void {
  updateMockData((data) => {
    const newUsers = data.users.filter((user) => user.id !== userId)
    const newApplicantStats = { ...data.applicantStats }
    const newJobEngines = { ...data.jobEngines }
    const newFiles = { ...data.files }
    const newMessages = { ...data.messages }
    
    delete newApplicantStats[userId]
    delete newJobEngines[userId]
    delete newFiles[userId]
    delete newMessages[userId]

    return {
      ...data,
      users: newUsers,
      advisorStats: {
        ...data.advisorStats,
        total_users: newUsers.length,
      },
      applicantStats: newApplicantStats,
      jobEngines: newJobEngines,
      files: newFiles,
      messages: newMessages,
    }
  })
}

export function addMockFile(userId: string, file: {
  file_name: string
  file_size: number
  upload_date: string
  file_type: string
  download_url?: string
  file_id?: string
}): void {
  updateMockData((data) => ({
    ...data,
    files: {
      ...data.files,
      [userId]: [...(data.files[userId] || []), {
        ...file,
        file_id: file.file_id || `${userId}_file_${Date.now()}`,
      }],
    },
  }))
}

export function removeMockFile(userId: string, fileName: string): void {
  updateMockData((data) => ({
    ...data,
    files: {
      ...data.files,
      [userId]: (data.files[userId] || []).filter((f) => f.file_name !== fileName),
    },
  }))
}

export function updateMockJobEngine(userId: string, jobEngine: any): void {
  updateMockData((data) => ({
    ...data,
    jobEngines: {
      ...data.jobEngines,
      [userId]: jobEngine,
    },
  }))
}

export function addMockMessage(userId: string, message: {
  id: string
  subject: string
  body: string
  created_at: string
  seen: boolean
  direction: "inbound" | "outbound"
  from?: string
  attachment?: string
}): void {
  updateMockData((data) => ({
    ...data,
    messages: {
      ...data.messages,
      [userId]: [...(data.messages[userId] || []), message].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    },
  }))
}

export function addMockJobMessage(userId: string, jobId: string, message: {
  id: string
  created_at: string
  message_id: string
  in_reply_to: string | null
  subject: string
  sender: string
  body: string
  thread_id: string
  inbox_id: string
  seen: boolean
  direction: "inbound" | "outbound"
  attachment: string | null
  to: string
  job_id: string
}): void {
  updateMockData((data) => {
    const jobKey = `${userId}_${jobId}`
    return {
      ...data,
      jobMessages: {
        ...data.jobMessages,
        [jobKey]: [...(data.jobMessages[jobKey] || []), message].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        ),
      },
    }
  })
}

export function updateMockJobStatus(userId: string, jobId: string, status: string): void {
  updateMockData((data) => ({
    ...data,
    jobHistory: {
      ...data.jobHistory,
      [userId]: (data.jobHistory[userId] || []).map((job: any) =>
        job.job_id === jobId
          ? {
              ...job,
              specifications: {
                ...job.specifications,
                application_status: status,
                selected: status === "applied",
                applied_at: status === "applied" ? new Date().toISOString() : job.specifications.applied_at,
              },
            }
          : job
      ),
    },
  }))
}

// Helper functions for products CRUD operations
export function getMockProducts(userId: string): PartnerProduct[] {
  const mockData = getMockData()
  return mockData.products[userId] || []
}

export function addMockProduct(product: PartnerProduct): void {
  updateMockData((data) => {
    const userId = product.user_id
    const existingProducts = data.products[userId] || []
    const newProduct: PartnerProduct = {
      ...product,
      product_id: product.product_id || `product-${userId}-${Date.now()}`,
      created_at: product.created_at || new Date().toISOString(),
      updated_at: product.updated_at || new Date().toISOString(),
    }
    return {
      ...data,
      products: {
        ...data.products,
        [userId]: [...existingProducts, newProduct],
      },
    }
  })
}

export function updateMockProduct(productId: string, updates: Partial<PartnerProduct>): void {
  updateMockData((data) => {
    const updatedProducts: Record<string, PartnerProduct[]> = {}
    for (const [userId, products] of Object.entries(data.products)) {
      updatedProducts[userId] = products.map((product) =>
        product.product_id === productId
          ? {
              ...product,
              ...updates,
              updated_at: new Date().toISOString(),
            }
          : product
      )
    }
    return {
      ...data,
      products: updatedProducts,
    }
  })
}

export function deleteMockProduct(productId: string): void {
  updateMockData((data) => {
    const updatedProducts: Record<string, PartnerProduct[]> = {}
    for (const [userId, products] of Object.entries(data.products)) {
      updatedProducts[userId] = products.filter((product) => product.product_id !== productId)
    }
    return {
      ...data,
      products: updatedProducts,
    }
  })
}

// Simulate API delay
export function simulateApiDelay(ms: number = 150): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

